import { PaymentRequestModal } from '@lightspeed/invoicing-library'
import React, { useCallback, useRef } from 'react'
import ReactDOM from 'react-dom'
import { ToastNotifications } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import isEqual from 'lodash/isEqual'
import { PaymentRequestFormData } from '../../../../../scripts/models/invoicing-payment-request'
import { saveSaleAndCreatePaymentRequest } from '../../../../functions/invoicing/save-sale-and-create-payment-request'
import {
  LAYBY_TERMINOLOGY_COUNTRY_IDS,
  buildPaymentRequestDto,
  buildRetailerDetails,
  filterLineItemsForPaymentRequest,
  getPreviewURL,
} from '../../../../functions/invoicing/payment-request-resources'
import {
  useRegisterManager,
  useRetailerSettingsManager,
  useSaleManager,
  useUserManager,
} from '../../../../ng-compat/hooks'
import { useAppDispatch, useAppSelector } from '../../../../state/hooks'
import {
  addPaymentRequestSaleAttribute,
  generateLoyaltyClaimCode,
  selectCustomer,
  selectLineItems,
  selectSaleAttributesHasPaymentRequest,
  selectSaleFinals,
  selectSaleId,
  selectSaleSummaryFields,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { useGoConfirmation } from '../hooks/useGoConfirmation'
import {
  selectTaxInformation,
  setPaymentRequestSuccess,
} from '../../../../state/slices/posState/posStateSlice'
import { useFetchPaymentTypes } from '../hooks/useFetchPaymentTypes'
import { useFulfillmentTakeNowSaleData } from '../../useFulfillmentTakeNowSaleData'
import { LineItem } from '../../../../models/line-item'

interface PaymentRequestModalProps {
  showPaymentRequestModal: boolean
  setShowPaymentRequestModal: () => void
  amountTendered: string // is the amount entered for partial payments on the Payment Panel, if not edited then equals the sale outstanding balance
  canLayaway?: boolean
  editMode?: boolean
  showShareableLink?: boolean
  defaultEmail?: string
  defaultNote?: string
  pricingTableTitle?: string
  onEditComplete?: (update: {
    email: string
    amount: string
    note: string
    isShareLink: boolean
  }) => void
}

export const PaymentRequestModalWrapper: React.FC<PaymentRequestModalProps> = ({
  showPaymentRequestModal,
  setShowPaymentRequestModal,
  amountTendered,
  canLayaway,
  editMode,
  showShareableLink: showShareableLinkProp,
  defaultEmail,
  defaultNote,
  pricingTableTitle,
  onEditComplete,
}) => {
  const t = useT()
  const registerManager = useRegisterManager()
  const currentOutlet = registerManager.getActiveOutlet()
  const usesLaybyTerminology = LAYBY_TERMINOLOGY_COUNTRY_IDS.includes(
    currentOutlet?.physical_country_id ?? ''
  )
  const currentRegister = registerManager.getActiveRegister()
  const customer = useAppSelector(selectCustomer)
  const saleLineItems = useAppSelector(selectLineItems, isEqual)
  const saleIdFromStore = useAppSelector(selectSaleId)
  const saleFinals = useAppSelector(selectSaleFinals)
  const retailer = useRetailerSettingsManager().get()
  const taxInformation = useAppSelector(selectTaxInformation)
  const { discount } = useAppSelector(selectSaleSummaryFields)
  const saleManager = useSaleManager()
  const dispatch = useAppDispatch()
  const goConfirmation = useGoConfirmation()
  const userManager = useUserManager()
  const user = userManager.getLoggedInUser()
  const hasPaymentRequestAttribute = useAppSelector(
    selectSaleAttributesHasPaymentRequest
  )
  const { systemPaymentTypes } = useFetchPaymentTypes()

  // When the share-link flow completes, we defer navigation until the
  // PayLinkGeneratedModal is closed so the merchant sees the link first.
  const pendingGoConfirmationRef = useRef(false)

  let lineItems: LineItem[] = []

  const {
    shouldShowGroupingOfSaleLines,
    takeNowLines: takeNowLinesData,
  } = useFulfillmentTakeNowSaleData()

  // For fulfillment with take now items, if grouping of sale lines is enabled,
  // filter out take now lines because they will be paid for up-front when the items goes with the customer
  // For other fulfillment items, remote payment request is allowed
  if (shouldShowGroupingOfSaleLines) {
    lineItems = filterLineItemsForPaymentRequest(
      saleLineItems,
      takeNowLinesData
    )
  } else {
    lineItems = saleLineItems
  }

  const layawayLabel = usesLaybyTerminology ? 'Layby' : 'Layaway'
  const pricingTitleBySaleType = canLayaway ? layawayLabel : 'Sale'

  const paymentRequestDto = buildPaymentRequestDto({
    customer,
    lineItems,
    saleIdFromStore,
    saleFinals,
    currentOutlet,
    currentRegister,
    retailer,
    discount,
    amountTendered,
    user,
    lspayPaymentTypeId: systemPaymentTypes.lspayPaymentType?.id,
    taxInformation,
    pricingTableTitle: pricingTableTitle ?? pricingTitleBySaleType,
  })

  if (editMode) {
    if (defaultEmail !== undefined) {
      paymentRequestDto.sendToEmail = defaultEmail
      paymentRequestDto.customer = {
        ...paymentRequestDto.customer,
        email: defaultEmail,
      }
    }
    if (defaultNote !== undefined) paymentRequestDto.note = defaultNote
  }

  const previewURL = getPreviewURL(retailer)

  const submitButtonText = editMode
    ? 'Confirm'
    : canLayaway
    ? 'Send request and Layaway'
    : undefined

  // add paymentrequest to sale attributes to indicate a payment request was created for this sale
  const addPaymentRequestAttribute = () => {
    if (!hasPaymentRequestAttribute) {
      dispatch(addPaymentRequestSaleAttribute())
    }
  }

  // this payment request success state is used to show info on the sale confirmation screen
  const setPaymentRequestSuccessState = (
    paymentRequestId: string,
    requestedAmount: number
  ) => {
    dispatch(
      setPaymentRequestSuccess({
        paymentRequestId,
        requestedAmount,
        ...(canLayaway && { headerTitle: 'Layaway' }),
      })
    )
  }

  const showFailureToast = useCallback(
    (error?: string) => {
      const errorMessage = error
        ? `Failed to create payment request: ${error}`
        : 'Failed to create payment request'
      ToastNotifications.negative(
        t(errorMessage, {
          _context: 'Payment request creation error toast',
        })
      )
    },
    [t]
  )

  const handleEditSubmit = (formData: PaymentRequestFormData) => {
    const { paymentRequestDto: dto } = formData
    const isShareLink = !dto.sendToEmail
    onEditComplete?.({
      email: dto.sendToEmail,
      amount: dto.requestedAmount.toFixed(2),
      note: dto.note ?? '',
      isShareLink,
    })
    setShowPaymentRequestModal()
  }

  const handleSubmit = async (formData: PaymentRequestFormData) => {
    // Add attribute to sale redux state
    addPaymentRequestAttribute()

    formData.paymentRequestDto.requestedAmount = Math.round(
      Number(formData.paymentRequestDto.requestedAmount || 0) * 100
    )

    const retailerDetails = buildRetailerDetails({ retailer })
    const result = await saveSaleAndCreatePaymentRequest(
      saleManager,
      formData.paymentRequestDto,
      retailerDetails
    )

    if (!result.success) {
      showFailureToast(result.error)
      return
    }

    // If canLayaway, transition sale to layby status AFTER payment request succeeds
    // This ensures we don't leave sale in layby status if payment request creation fails
    if (canLayaway) {
      // mirror logic from useLaybyOnAccountSale hook for handling layby sales
      dispatch(generateLoyaltyClaimCode())
      await saleManager.laybySale()
    }

    setPaymentRequestSuccessState(
      result.paymentRequestId!,
      result.requestedAmount || formData.paymentRequestDto.requestedAmount
    )

    // Share link flow — return proposalUrl so MFE shows the PayLinkGeneratedModal,
    // and defer navigation until that modal is closed via handleModalClose.
    if (!formData.paymentRequestDto.sendToEmail && result.proposalUrl) {
      pendingGoConfirmationRef.current = true
      return { proposalUrl: result.proposalUrl }
    }

    // Navigate to the sale confirmation screen.
    goConfirmation()

    // Close modal
    setShowPaymentRequestModal()
  }

  const handleModalClose = () => {
    setShowPaymentRequestModal()
    if (pendingGoConfirmationRef.current) {
      pendingGoConfirmationRef.current = false
      goConfirmation()
    }
  }

  if (!showPaymentRequestModal) return null

  return ReactDOM.createPortal(
    <PaymentRequestModal
      open={showPaymentRequestModal}
      setOpen={handleModalClose}
      customerEmail={editMode ? defaultEmail ?? '' : ''}
      setCustomerEmail={(_value: string) => {}} // No-op function as modal handles its own state
      documentURL={previewURL}
      onSubmit={editMode ? handleEditSubmit : handleSubmit}
      handleCancel={handleModalClose}
      paymentRequestDto={paymentRequestDto}
      showAmountField={false}
      showItemsToggle={false}
      {...(submitButtonText && { submitButtonText })}
      showShareableLink={showShareableLinkProp ?? !editMode}
    />,
    document.body
  )
}
